import React, { createContext, useContext, useReducer } from 'react';

interface SocialState {
  likes: Record<string, boolean>;
  reposts: Record<string, boolean>;
  follows: Record<string, boolean>;
  playlistFollows: Record<string, boolean>;
  comments: Record<string, Comment[]>;
  userPlaylists: UserPlaylist[];
}

interface Comment {
  id: string;
  userId: string;
  userName: string;
  userAvatar: string;
  content: string;
  timestamp: number;
  likes: number;
  song?: any;
}

interface UserPlaylist {
  id: string;
  name: string;
  songs: string[];
}

type SocialAction =
  | { type: 'TOGGLE_LIKE'; payload: string }
  | { type: 'TOGGLE_REPOST'; payload: string }
  | { type: 'TOGGLE_FOLLOW'; payload: string }
  | { type: 'TOGGLE_PLAYLIST_FOLLOW'; payload: string }
  | { type: 'ADD_COMMENT'; payload: { targetId: string; comment: Comment } }
  | { type: 'ADD_TO_PLAYLIST'; payload: { playlistId: string; songId: string } }
  | { type: 'CREATE_PLAYLIST'; payload: UserPlaylist }
  | { type: 'REMOVE_FROM_PLAYLIST'; payload: { playlistId: string; songId: string } };

const initialState: SocialState = {
  likes: {},
  reposts: {},
  follows: {},
  playlistFollows: {},
  comments: {},
  userPlaylists: []
};

const socialReducer = (state: SocialState, action: SocialAction): SocialState => {
  switch (action.type) {
    case 'TOGGLE_LIKE':
      return {
        ...state,
        likes: {
          ...state.likes,
          [action.payload]: !state.likes[action.payload]
        }
      };
    case 'TOGGLE_REPOST':
      return {
        ...state,
        reposts: {
          ...state.reposts,
          [action.payload]: !state.reposts[action.payload]
        }
      };
    case 'TOGGLE_FOLLOW':
      return {
        ...state,
        follows: {
          ...state.follows,
          [action.payload]: !state.follows[action.payload]
        }
      };
    case 'TOGGLE_PLAYLIST_FOLLOW':
      return {
        ...state,
        playlistFollows: {
          ...state.playlistFollows,
          [action.payload]: !state.playlistFollows[action.payload]
        }
      };
    case 'ADD_COMMENT':
      return {
        ...state,
        comments: {
          ...state.comments,
          [action.payload.targetId]: [
            ...(state.comments[action.payload.targetId] || []),
            action.payload.comment
          ]
        }
      };
    case 'CREATE_PLAYLIST':
      return {
        ...state,
        userPlaylists: [...state.userPlaylists, action.payload]
      };
    case 'ADD_TO_PLAYLIST':
      return {
        ...state,
        userPlaylists: state.userPlaylists.map(playlist =>
          playlist.id === action.payload.playlistId
            ? { ...playlist, songs: [...playlist.songs, action.payload.songId] }
            : playlist
        )
      };
    case 'REMOVE_FROM_PLAYLIST':
      return {
        ...state,
        userPlaylists: state.userPlaylists.map(playlist =>
          playlist.id === action.payload.playlistId
            ? { ...playlist, songs: playlist.songs.filter(id => id !== action.payload.songId) }
            : playlist
        )
      };
    default:
      return state;
  }
};

const SocialContext = createContext<{
  state: SocialState;
  dispatch: React.Dispatch<SocialAction>;
} | null>(null);

export const SocialProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [state, dispatch] = useReducer(socialReducer, initialState);

  return (
    <SocialContext.Provider value={{ state, dispatch }}>
      {children}
    </SocialContext.Provider>
  );
};

export const useSocial = () => {
  const context = useContext(SocialContext);
  if (!context) {
    throw new Error('useSocial must be used within a SocialProvider');
  }
  return context;
};